/**
 * WebRTC QR utility functions - Native Implementation
 * Replaces WASM dependencies with native browser APIs
 */

// Import native WebRTC manager
import { webrtcManager } from './webrtc-native'
import pkg from '../../package.json'

// Canonical QR string format, shared with the Rust side (src/qr.rs):
//   'W' + base64url(bytes) without padding.
// This file used btoa/atob (standard alphabet, padded), which the Rust
// decoder cannot read, and which broke in two more ways: btoa throws on any
// character outside Latin-1 (a Turkish room name such as "Toplantı" made QR
// generation fail), and decode passed atob's binary string through
// TextEncoder, turning every byte >= 0x80 into two bytes.
const QR_PREFIX = 'W'

/** Fields `validateQRString` inspects in a decoded QR payload. */
interface ParsedQRPayload {
  type?: string
  offer?: unknown
  answer?: unknown
  roomId?: unknown
  connectionId?: unknown
  id?: unknown
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64UrlToBytes(text: string): Uint8Array {
  const body = text.replace(/=+$/, '')
  if (!/^[A-Za-z0-9_-]*$/.test(body)) {
    throw new Error('QR data is not base64url')
  }
  const standard = body.replace(/-/g, '+').replace(/_/g, '/')
  const padded = standard + '='.repeat((4 - (standard.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

// QR validation utilities
export const qrUtils = {
  /**
   * Validate if a QR string is in WebRTC format
   */
  validateQRString(qrString: string): boolean {
    try {
      // Basic validation for WebRTC QR format
      if (!qrString || typeof qrString !== 'string') {
        return false
      }

      let parsed: ParsedQRPayload | null = null
      if (qrString.startsWith(QR_PREFIX)) {
        parsed = JSON.parse(new TextDecoder().decode(this.decodeFromQRString(qrString)))
      } else if (qrString.startsWith('{')) {
        parsed = JSON.parse(qrString)
      } else {
        return false
      }

      // Check if it has WebRTC-related fields
      return Boolean(
        parsed && (
          (parsed.type && (
            parsed.type.includes('webrtc') ||
            parsed.type.includes('connection') ||
            parsed.type.includes('invite') ||
            parsed.type.includes('offer') ||
            parsed.type.includes('answer')
          )) ||
          parsed.offer ||
          parsed.answer ||
          parsed.roomId ||
          parsed.connectionId ||
          parsed.id
        )
      )
    } catch {
      return false
    }
  },

  /**
   * Decode QR string to get raw data
   */
  decodeFromQRString(qrString: string): Uint8Array {
    if (!qrString.startsWith(QR_PREFIX)) {
      throw new Error('Invalid QR format - missing prefix')
    }
    return base64UrlToBytes(qrString.substring(QR_PREFIX.length))
  },

  /**
   * Encode to the canonical QR string. Raw bytes are encoded as-is; any other
   * value is JSON-serialised and UTF-8 encoded first. (Passing a Uint8Array
   * used to JSON-serialise it into {"0":..,"1":..}.)
   */
  encodeToQRString(data: unknown): string {
    const bytes = data instanceof Uint8Array
      ? data
      : new TextEncoder().encode(JSON.stringify(data))
    return QR_PREFIX + bytesToBase64Url(bytes)
  },

  /**
   * Extract WebRTC session description from QR data
   */
  extractSessionDescription(qrString: string): RTCSessionDescriptionInit | null {
    try {
      if (!this.validateQRString(qrString)) {
        return null
      }

      const decodedData = this.decodeFromQRString(qrString)
      const text = new TextDecoder().decode(decodedData)
      const parsed = JSON.parse(text)

      // Look for offer or answer in the parsed data
      if (parsed.offer) {
        return parsed.offer
      }

      if (parsed.answer) {
        return parsed.answer
      }

      // If no explicit session description, create one from the data
      if (parsed.type === 'webrtc-offer') {
        return {
          type: 'offer',
          sdp: JSON.stringify(parsed)
        }
      }

      return null
    } catch (error) {
      console.error('Failed to extract session description:', error)
      return null
    }
  }
}

// Browser WebRTC Manager (for backward compatibility)
export class BrowserWebRTCManager {
  private nativeManager: typeof webrtcManager

  constructor() {
    this.nativeManager = webrtcManager
  }

  async createHostConnection(connectionData: string, message: string) {
    return this.nativeManager.createHostConnection(connectionData, message)
  }

  async acceptConnection(qrData: string) {
    return this.nativeManager.acceptConnection(qrData)
  }

  async completeConnection(connectionId: string, answerData: string) {
    return this.nativeManager.completeConnection(connectionId, answerData)
  }

  sendMessage(connectionId: string, message: string): boolean {
    return this.nativeManager.sendMessage(connectionId, message)
  }

  getConnectionState(connectionId: string): string {
    return this.nativeManager.getConnectionState(connectionId)
  }

  closeConnection(connectionId: string): void {
    this.nativeManager.closeConnection(connectionId)
  }

  getActiveConnections(): string[] {
    return this.nativeManager.getActiveConnections()
  }

  getSelectedPath(connectionId: string) {
    return this.nativeManager.getSelectedPath(connectionId)
  }

  getIceCandidateErrors(connectionId: string) {
    return this.nativeManager.getIceCandidateErrors(connectionId)
  }

  setIceConfiguration(connectionId: string, config: Pick<RTCConfiguration, 'iceServers' | 'iceTransportPolicy'>) {
    this.nativeManager.setIceConfiguration(connectionId, config)
  }

  restartIce(connectionId: string) {
    return this.nativeManager.restartIce(connectionId)
  }

  acceptIceRestart(connectionId: string, restartData: string) {
    return this.nativeManager.acceptIceRestart(connectionId, restartData)
  }
}

// Was a hardcoded '2.0.0-native' while package.json said 0.1.1.
export function getVersion(): string {
  return `${pkg.version}-native`
}

// Was a hardcoded 'v2.0' that no payload carries. The QR payload has no
// version field; its only format identifier is the `type` it declares.
export function getProtocolVersion(): string {
  return 'webrtc-qr-connection'
}

// Export native manager as default
export default webrtcManager